/**
 * IndexedDB Service for offline-first data storage
 *
 * Provides persistent storage for cached API responses, offline queue items,
 * and user data using IndexedDB. Includes automatic cleanup and storage quota management.
 *
 * References:
 * - src/services/socketService.js - Service pattern
 * - packages/schema/src/caching/v1.ts - Schema validation
 */

import logger from '../utils/logger';

class IndexedDBService {
  constructor() {
    this.dbName = 'VirtualHardhatDB';
    this.version = 2;
    this.db = null;
    this.isInitialized = false;
    this.isSupported = this.checkIndexedDBSupport();
  }

  /**
   * Check if IndexedDB is supported in the current environment
   * @returns {boolean} True if IndexedDB is supported
   */
  checkIndexedDBSupport() {
    return typeof window !== 'undefined' &&
           'indexedDB' in window &&
           'open' in window.indexedDB;
  }

  /**
   * Open or create the IndexedDB database
   * @returns {Promise<IDBDatabase>} Database instance
   */
  async openDatabase() {
    if (!this.isSupported) {
      logger.warn('[IndexedDBService] IndexedDB not supported, falling back to in-memory storage');
      return null;
    }

    if (this.db) {
      return this.db;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => {
        logger.error('[IndexedDBService] Failed to open database:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.isInitialized = true;
        logger.info('[IndexedDBService] Database opened successfully');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        const oldVersion = event.oldVersion || 0;

        // Create object stores
        if (!db.objectStoreNames.contains('cachedResponses')) {
          const cachedResponsesStore = db.createObjectStore('cachedResponses', { keyPath: 'id' });
          cachedResponsesStore.createIndex('url', 'url', { unique: false });
          cachedResponsesStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        if (!db.objectStoreNames.contains('offlineQueue')) {
          const offlineQueueStore = db.createObjectStore('offlineQueue', { keyPath: 'id' });
          offlineQueueStore.createIndex('status', 'status', { unique: false });
          offlineQueueStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        if (!db.objectStoreNames.contains('userData')) {
          db.createObjectStore('userData', { keyPath: 'key' });
        }

        if (!db.objectStoreNames.contains('navigationPatterns')) {
          const navigationPatternsStore = db.createObjectStore('navigationPatterns', { keyPath: 'id' });
          navigationPatternsStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        // Version 2: Add dedicated stores for submissions and workflows
        if (oldVersion < 2) {
          // Create offlineSubmissions store
          if (!db.objectStoreNames.contains('offlineSubmissions')) {
            const submissionsStore = db.createObjectStore('offlineSubmissions', { keyPath: 'id' });
            submissionsStore.createIndex('status', 'status', { unique: false });
            submissionsStore.createIndex('timestamp', 'timestamp', { unique: false });
            submissionsStore.createIndex('type', 'type', { unique: false });
            submissionsStore.createIndex('priority', 'priority', { unique: false });
          }

          // Create offlineWorkflows store
          if (!db.objectStoreNames.contains('offlineWorkflows')) {
            const workflowsStore = db.createObjectStore('offlineWorkflows', { keyPath: 'id' });
            workflowsStore.createIndex('status', 'status', { unique: false });
            workflowsStore.createIndex('timestamp', 'timestamp', { unique: false });
            workflowsStore.createIndex('workflowType', 'workflowType', { unique: false });
          }

          // Create conflictLog store
          if (!db.objectStoreNames.contains('conflictLog')) {
            const conflictLogStore = db.createObjectStore('conflictLog', { keyPath: 'id' });
            conflictLogStore.createIndex('timestamp', 'timestamp', { unique: false });
            conflictLogStore.createIndex('resolved', 'resolved', { unique: false });
            conflictLogStore.createIndex('submissionId', 'submissionId', { unique: false });
          }
        }

        logger.info('[IndexedDBService] Database schema created/updated');
      };
    });
  }

  /**
   * Set an item in the specified object store
   * @param {string} storeName - Name of the object store
   * @param {string} key - Key for the item
   * @param {any} value - Value to store
   * @returns {Promise<void>}
   */
  async set(storeName, key, value) {
    const db = await this.openDatabase();
    if (!db) return;

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put({ ...value, id: key });

      request.onsuccess = () => resolve();
      request.onerror = () => {
        logger.error(`[IndexedDBService] Failed to set ${storeName}:${key}:`, request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get an item from the specified object store
   * @param {string} storeName - Name of the object store
   * @param {string} key - Key for the item
   * @returns {Promise<any|null>} Stored value or null if not found
   */
  async get(storeName, key) {
    const db = await this.openDatabase();
    if (!db) return null;

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => {
        logger.error(`[IndexedDBService] Failed to get ${storeName}:${key}:`, request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get all items from the specified object store matching an index
   * @param {string} storeName - Name of the object store
   * @param {string} indexName - Name of the index
   * @param {any} indexValue - Value to match in the index
   * @returns {Promise<Array>} Array of matching items
   */
  async getAll(storeName, indexName, indexValue) {
    const db = await this.openDatabase();
    if (!db) return [];

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const index = store.index(indexName);
      const request = index.getAll(indexValue);

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => {
        logger.error(`[IndexedDBService] Failed to getAll ${storeName}:${indexName}=${indexValue}:`, request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Delete an item from the specified object store
   * @param {string} storeName - Name of the object store
   * @param {string} key - Key for the item
   * @returns {Promise<void>}
   */
  async delete(storeName, key) {
    const db = await this.openDatabase();
    if (!db) return;

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(key);

      request.onsuccess = () => resolve();
      request.onerror = () => {
        logger.error(`[IndexedDBService] Failed to delete ${storeName}:${key}:`, request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Clear all items from the specified object store
   * @param {string} storeName - Name of the object store
   * @returns {Promise<void>}
   */
  async clear(storeName) {
    const db = await this.openDatabase();
    if (!db) return;

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => {
        logger.error(`[IndexedDBService] Failed to clear ${storeName}:`, request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Cache an API response with TTL
   * @param {string} url - API endpoint URL
   * @param {string} method - HTTP method
   * @param {any} data - Response data
   * @param {number} ttl - Time-to-live in seconds (default: 5 minutes)
   * @returns {Promise<void>}
   */
  async cacheResponse(url, method, data, ttl = 300) {
    const cacheKey = `${method}:${url}`;
    const cacheEntry = {
      url,
      method,
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + (ttl * 1000),
    };

    await this.set('cachedResponses', cacheKey, cacheEntry);
  }

  /**
   * Get a cached API response if not expired
   * @param {string} url - API endpoint URL
   * @param {string} method - HTTP method
   * @returns {Promise<any|null>} Cached data or null if expired/not found
   */
  async getCachedResponse(url, method) {
    const cacheKey = `${method}:${url}`;
    const cached = await this.get('cachedResponses', cacheKey);

    if (!cached || cached.expiresAt < Date.now()) {
      if (cached && cached.expiresAt < Date.now()) {
        // Clean up expired entry
        await this.delete('cachedResponses', cacheKey);
      }
      return null;
    }

    return cached.data;
  }

  /**
   * Clean up expired cache entries
   * @returns {Promise<void>}
   */
  async cleanExpiredCache() {
    const db = await this.openDatabase();
    if (!db) return;

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['cachedResponses'], 'readwrite');
      const store = transaction.objectStore('cachedResponses');
      const request = store.openCursor();

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const entry = cursor.value;
          if (entry.expiresAt < Date.now()) {
            cursor.delete();
          }
          cursor.continue();
        } else {
          resolve();
        }
      };

      request.onerror = () => {
        logger.error('[IndexedDBService] Failed to clean expired cache:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Add an item to the offline queue
   * @param {Object} item - Queue item (validated with OfflineQueueItemSchema)
   * @returns {Promise<void>}
   */
  async addToQueue(item) {
    const queueItem = {
      ...item,
      id: item.id || `queue-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };

    await this.set('offlineQueue', queueItem.id, queueItem);
  }

  /**
   * Get offline queue items by status
   * @param {string} status - Status to filter by ('pending', 'syncing', 'synced', 'failed')
   * @returns {Promise<Array>} Array of queue items
   */
  async getQueueItems(status) {
    return await this.getAll('offlineQueue', 'status', status);
  }

  /**
   * Update a queue item
   * @param {string} id - Queue item ID
   * @param {Object} updates - Updates to apply
   * @returns {Promise<void>}
   */
  async updateQueueItem(id, updates) {
    const existing = await this.get('offlineQueue', id);
    if (existing) {
      const updated = { ...existing, ...updates };
      await this.set('offlineQueue', id, updated);
    }
  }

  /**
   * Remove an item from the offline queue
   * @param {string} id - Queue item ID
   * @returns {Promise<void>}
   */
  async removeFromQueue(id) {
    await this.delete('offlineQueue', id);
  }

  /**
   * Get storage quota information
   * @returns {Promise<Object>} Storage estimate with usage, quota, and percent used
   */
  async getStorageEstimate() {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      return {
        usage: estimate.usage || 0,
        quota: estimate.quota || 0,
        percentUsed: estimate.quota ? (estimate.usage / estimate.quota) * 100 : 0,
      };
    }

    return { usage: 0, quota: 0, percentUsed: 0 };
  }

  /**
   * Add a submission to offlineSubmissions store
   * @param {Object} submission - Submission data
   * @returns {Promise<string>} Submission ID
   */
  async addSubmission(submission) {
    const db = await this.openDatabase();
    if (!db) throw new Error('IndexedDB not available');

    // Generate UUID if not provided
    const id = submission.id || `sub-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Determine priority based on type
    const priorityMap = {
      incident: 'high',
      hazard: 'medium',
      permit: 'low',
      workflow: 'medium',
    };
    const priority = priorityMap[submission.type] || 'medium';

    const submissionRecord = {
      id,
      type: submission.type || 'incident',
      data: submission.data || submission,
      status: 'pending',
      timestamp: new Date().toISOString(),
      priority,
      retryCount: 0,
      ...submission,
    };

    await this.set('offlineSubmissions', id, submissionRecord);
    return id;
  }

  /**
   * Get submissions by status
   * @param {string} status - Status to filter by
   * @returns {Promise<Array>} Array of submissions
   */
  async getSubmissionsByStatus(status) {
    return await this.getAll('offlineSubmissions', 'status', status);
  }

  /**
   * Get submissions by type
   * @param {string} type - Type to filter by
   * @returns {Promise<Array>} Array of submissions
   */
  async getSubmissionsByType(type) {
    return await this.getAll('offlineSubmissions', 'type', type);
  }

  /**
   * Update submission status
   * @param {string} id - Submission ID
   * @param {string} status - New status
   * @param {Object} metadata - Optional metadata (syncedAt, error, retryCount)
   * @returns {Promise<void>}
   */
  async updateSubmissionStatus(id, status, metadata = {}) {
    const existing = await this.get('offlineSubmissions', id);
    if (existing) {
      const updated = {
        ...existing,
        status,
        ...metadata,
      };
      await this.set('offlineSubmissions', id, updated);
    }
  }

  /**
   * Remove a submission
   * @param {string} id - Submission ID
   * @returns {Promise<void>}
   */
  async removeSubmission(id) {
    await this.delete('offlineSubmissions', id);
  }

  /**
   * Add a workflow to offlineWorkflows store
   * @param {Object} workflow - Workflow data
   * @returns {Promise<string>} Workflow ID
   */
  async addWorkflow(workflow) {
    const db = await this.openDatabase();
    if (!db) throw new Error('IndexedDB not available');

    const id = workflow.id || `wf-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    const workflowRecord = {
      id,
      workflowType: workflow.workflowType || workflow.type || 'safety',
      data: workflow.data || workflow,
      status: 'pending',
      timestamp: new Date().toISOString(),
      retryCount: 0,
      ...workflow,
    };

    await this.set('offlineWorkflows', id, workflowRecord);
    return id;
  }

  /**
   * Get workflows by status
   * @param {string} status - Status to filter by
   * @returns {Promise<Array>} Array of workflows
   */
  async getWorkflowsByStatus(status) {
    return await this.getAll('offlineWorkflows', 'status', status);
  }

  /**
   * Update workflow status
   * @param {string} id - Workflow ID
   * @param {string} status - New status
   * @param {Object} metadata - Optional metadata
   * @returns {Promise<void>}
   */
  async updateWorkflowStatus(id, status, metadata = {}) {
    const existing = await this.get('offlineWorkflows', id);
    if (existing) {
      const updated = {
        ...existing,
        status,
        ...metadata,
      };
      await this.set('offlineWorkflows', id, updated);
    }
  }

  /**
   * Batch add submissions
   * @param {Array} submissions - Array of submission objects
   * @returns {Promise<Array>} Array of submission IDs
   */
  async batchAddSubmissions(submissions) {
    const db = await this.openDatabase();
    if (!db) throw new Error('IndexedDB not available');

    const transaction = db.transaction(['offlineSubmissions'], 'readwrite');
    const store = transaction.objectStore('offlineSubmissions');
    const ids = [];

    return new Promise((resolve, reject) => {
      let completed = 0;
      const total = submissions.length;

      if (total === 0) {
        resolve([]);
        return;
      }

      submissions.forEach((submission) => {
        const id = submission.id || `sub-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const priorityMap = {
          incident: 'high',
          hazard: 'medium',
          permit: 'low',
          workflow: 'medium',
        };
        const priority = priorityMap[submission.type] || 'medium';

        const submissionRecord = {
          id,
          type: submission.type || 'incident',
          data: submission.data || submission,
          status: 'pending',
          timestamp: new Date().toISOString(),
          priority,
          retryCount: 0,
          ...submission,
        };

        const request = store.add(submissionRecord);
        request.onsuccess = () => {
          ids.push(id);
          completed++;
          if (completed === total) {
            resolve(ids);
          }
        };
        request.onerror = () => reject(request.error);
      });
    });
  }

  /**
   * Batch update statuses
   * @param {Array} updates - Array of {id, status, metadata} objects
   * @returns {Promise<void>}
   */
  async batchUpdateStatuses(updates) {
    const db = await this.openDatabase();
    if (!db) throw new Error('IndexedDB not available');

    const transaction = db.transaction(['offlineSubmissions', 'offlineWorkflows'], 'readwrite');
    const submissionsStore = transaction.objectStore('offlineSubmissions');
    const workflowsStore = transaction.objectStore('offlineWorkflows');

    return new Promise((resolve, reject) => {
      let completed = 0;
      const total = updates.length;

      if (total === 0) {
        resolve();
        return;
      }

      updates.forEach((update) => {
        const store = update.store === 'workflows' ? workflowsStore : submissionsStore;
        const getRequest = store.get(update.id);

        getRequest.onsuccess = () => {
          const existing = getRequest.result;
          if (existing) {
            const updated = {
              ...existing,
              status: update.status,
              ...update.metadata,
            };
            const putRequest = store.put(updated);
            putRequest.onsuccess = () => {
              completed++;
              if (completed === total) {
                resolve();
              }
            };
            putRequest.onerror = () => reject(putRequest.error);
          } else {
            completed++;
            if (completed === total) {
              resolve();
            }
          }
        };
        getRequest.onerror = () => reject(getRequest.error);
      });
    });
  }

  /**
   * Log a conflict
   * @param {Object} conflictData - Conflict details
   * @returns {Promise<string>} Conflict ID
   */
  async logConflict(conflictData) {
    const db = await this.openDatabase();
    if (!db) throw new Error('IndexedDB not available');

    const id = conflictData.id || `conflict-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    const conflict = {
      id,
      submissionId: conflictData.submissionId,
      serverVersion: conflictData.serverVersion,
      clientVersion: conflictData.clientVersion,
      strategy: conflictData.strategy || 'pending',
      resolvedData: conflictData.resolvedData,
      resolved: false,
      timestamp: new Date().toISOString(),
      ...conflictData,
    };

    await this.set('conflictLog', id, conflict);
    return id;
  }

  /**
   * Get unresolved conflicts
   * @returns {Promise<Array>} Array of unresolved conflicts
   */
  async getUnresolvedConflicts() {
    const db = await this.openDatabase();
    if (!db) return [];

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['conflictLog'], 'readonly');
      const store = transaction.objectStore('conflictLog');
      const index = store.index('resolved');
      const request = index.getAll(false);

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get storage breakdown by store
   * @returns {Promise<Object>} Storage estimates per store
   */
  async getStorageBreakdown() {
    const db = await this.openDatabase();
    if (!db) return {};

    const stores = [
      'cachedResponses',
      'offlineQueue',
      'offlineSubmissions',
      'offlineWorkflows',
      'conflictLog',
      'userData',
      'navigationPatterns',
    ];

    const breakdown = {};

    for (const storeName of stores) {
      if (db.objectStoreNames.contains(storeName)) {
        const transaction = db.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        const request = store.getAll();

        await new Promise((resolve, reject) => {
          request.onsuccess = () => {
            const items = request.result || [];
            // Rough estimate: JSON.stringify size
            breakdown[storeName] = {
              count: items.length,
              estimatedSize: JSON.stringify(items).length,
            };
            resolve();
          };
          request.onerror = () => reject(request.error);
        });
      }
    }

    return breakdown;
  }

  /**
   * Clear old data to free up storage space
   * @param {number} daysToKeep - Number of days of data to keep (default: 30)
   * @returns {Promise<void>}
   */
  async clearOldData(daysToKeep = 30) {
    const cutoffTime = Date.now() - (daysToKeep * 24 * 60 * 60 * 1000);

    // Clear old cached responses
    const cachedResponses = await this.getAll('cachedResponses', 'timestamp', null);
    for (const entry of cachedResponses) {
      if (entry.timestamp < cutoffTime) {
        await this.delete('cachedResponses', entry.id);
      }
    }

    // Clear old navigation patterns
    const navigationPatterns = await this.getAll('navigationPatterns', 'timestamp', null);
    for (const pattern of navigationPatterns) {
      if (pattern.timestamp < cutoffTime) {
        await this.delete('navigationPatterns', pattern.id);
      }
    }

    // Clear old synced submissions (keep pending/failed)
    const syncedSubmissions = await this.getSubmissionsByStatus('synced');
    for (const submission of syncedSubmissions) {
      const submissionTime = new Date(submission.timestamp).getTime();
      if (submissionTime < cutoffTime) {
        await this.removeSubmission(submission.id);
      }
    }

    // Clear old synced workflows
    const syncedWorkflows = await this.getWorkflowsByStatus('synced');
    for (const workflow of syncedWorkflows) {
      const workflowTime = new Date(workflow.timestamp).getTime();
      if (workflowTime < cutoffTime) {
        await this.delete('offlineWorkflows', workflow.id);
      }
    }

    // Clear old resolved conflicts
    const db = await this.openDatabase();
    if (db && db.objectStoreNames.contains('conflictLog')) {
      const transaction = db.transaction(['conflictLog'], 'readwrite');
      const store = transaction.objectStore('conflictLog');
      const index = store.index('resolved');
      const request = index.openCursor(IDBKeyRange.only(true));

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const conflict = cursor.value;
          const conflictTime = new Date(conflict.timestamp).getTime();
          if (conflictTime < cutoffTime) {
            cursor.delete();
          }
          cursor.continue();
        }
      };
    }

    logger.info(`[IndexedDBService] Cleared data older than ${daysToKeep} days`);
  }
}

// Export singleton instance
const indexedDBService = new IndexedDBService();
export default indexedDBService;
